import React, { useEffect } from 'react';

import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { FormikHelpers } from 'formik';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  userUpdate,
  userGetCurrent,
  setOperationStatus,
  UI_OPERATION_STATUS_ERROR,
  UI_OPERATION_STATUS_SUCCESS,
} from '@uncinc/react-drupal-core';
import type { ImmutableRootState } from '@uncinc/react-drupal-core/types';
import { FormikWrapper, FormTextField, Button } from '@uncinc/uncinc-react-kitchen-sink';

import useDispatch from '../../../../hooks/useTypedDispatch';
import InformationOverlay from '../../../../components/InformationOverlay';
import { MINIMUM_PASSWORD_CHARACTER_COUNT } from '../../../../config/constants';
import { ROUTE_CUSTOMER_SERVICE } from '../../../../config/routes';
import { clearPageInformation, setPageInformation, setPageViewSent } from '../../../../store/actions/UI';

import './style.scss';

type FormValues = {
  mail: string,
  password?: string,
  new_password?: string,
  new_password_confirm?: string,
};

const OverviewAccountPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const uid = useSelector<ImmutableRootState<number>, number>((state) => state.getIn(['login', 'uid'], 0));
  const initialValues = useSelector<ImmutableRootState<string>, FormValues>((state) => ({
    mail: state.getIn(['user', uid, 'mail', 0, 'value'], ''),
    password: '',
    newPassword: '',
    newPasswordConfirm: '',
  }));

  const userUpdateStatus = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['ui', 'user', 'operations', 'update', 'status'], ''));
  const profileUpdateStatus = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['ui', 'profile', 'operations', 'update', 'status'], ''));

  useEffect(() => {
    dispatch(userGetCurrent());
    dispatch(clearPageInformation());
    dispatch(setPageInformation(t('mijn_vnl.overview.title')));
    dispatch(setPageViewSent(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validationSchema = Yup.object({
    mail: Yup.string()
      .email(t('mijn_vnl.info.email.invalid'))
      .required(t('mijn_vnl.info.email.required')),
    password: Yup.string().max(255).when(['new_password'], {
      is: (new_password: string) => new_password?.length > 0,
      then: Yup.string().required(t('mijn_vnl.overview.password.password.required')),
    }),
    new_password: Yup.string().max(255)
      .min(
        MINIMUM_PASSWORD_CHARACTER_COUNT,
        t('mijn_vnl.overview.password.new_password.required', { count: MINIMUM_PASSWORD_CHARACTER_COUNT }),
      )
      .matches(/^(?=.*\d)(?=.*[a-zA-Z]).*$/, t('mijn_vnl.overview.password.new_password.required', { count: MINIMUM_PASSWORD_CHARACTER_COUNT })),
    new_password_confirm: Yup.string().max(255).when(['new_password'], {
      is: (new_password: string) => new_password?.length > 0,
      then: Yup.string()
        .oneOf([Yup.ref('new_password'), null], t('mijn_vnl.overview.password.new_password_confirm.no_match'))
        .required(t('mijn_vnl.overview.password.new_password_confirm.required')),
    }),
  });

  const onSubmit = (
    {
      password,
      new_password,
    }: FormValues,
    {
      resetForm,
    }: FormikHelpers<FormValues>,
  ) => {
    if (password && new_password) {
      dispatch(userUpdate(uid, {
        pass: [
          {
            existing: password,
            value: new_password,
          },
        ],
      }));
    }
    resetForm();
  };

  // Listens to the update status and shows a success or error toast message.
  useEffect(() => {
    if (profileUpdateStatus !== null) {
      if (profileUpdateStatus === UI_OPERATION_STATUS_SUCCESS) {
        toast.success(t('form.update_success'));
      } else if (profileUpdateStatus === UI_OPERATION_STATUS_ERROR) {
        toast.error(t('form.update_error'));
      }
      dispatch(setOperationStatus('profile', 'update', null, null));
    }
    if (userUpdateStatus !== null) {
      if (userUpdateStatus === UI_OPERATION_STATUS_SUCCESS) {
        toast.success(t('mijn_vnl.overview.password_success'));
      } else if (userUpdateStatus === UI_OPERATION_STATUS_ERROR) {
        toast.error(t('mijn_vnl.overview.password_error'));
      }
      dispatch(setOperationStatus('user', 'update', null, null));
    }
  }, [profileUpdateStatus, userUpdateStatus, dispatch, t]);

  // Check if we have finished getting the user data before rendering the form.
  if (!initialValues.mail) {
    return null;
  }

  return (
    <div className="AccountOverview">
      <FormikWrapper
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        <div className="AccountOverview__content">
          <label htmlFor="email">{t('mijn_vnl.info.mail.label')}</label>
          <FormTextField
            fieldName="mail"
            type="email"
            disabled
          />
          <InformationOverlay
            overlayKey="user_edit_connected_accounts_info_overlay"
            title={t('mijn_vnl.update_email_overlay.title')}
            body={t('mijn_vnl.update_email_overlay.body', { customerServiceUrl: ROUTE_CUSTOMER_SERVICE })}
            buttonTitle={t('mijn_vnl.update_email_overlay.title')}
          />
        </div>
        <div className="AccountOverview__content">
          <FormTextField
            title={t('mijn_vnl.overview.password.password.label')}
            fieldName="password"
            type="password"
            data-lpignore
            showPasswordReveal
            autocomplete="off"
          />
          <FormTextField
            title={t('mijn_vnl.overview.password.new_password.label')}
            fieldName="new_password"
            type="password"
            data-lpignore
            showPasswordReveal
            autocomplete="off"
            description={t('mijn_vnl.overview.password.new_password.minimum', { count: MINIMUM_PASSWORD_CHARACTER_COUNT })}
          />
          <FormTextField
            title={t('mijn_vnl.overview.password.new_password_confirm.label')}
            fieldName="new_password_confirm"
            type="password"
            showPasswordReveal
            autocomplete="off"
          />
        </div>
        <div className="Form__actions">
          <Button
            type="submit"
            className="Button Button--primary"
          >
            {t('mijn_vnl.overview.submit')}
          </Button>
        </div>
        <InformationOverlay
          overlayKey="user_edit_connected_accounts_info_overlay"
          title={t('mijn_vnl.delete_account_overlay.title')}
          body={t('mijn_vnl.delete_account_overlay.body', { customerServiceUrl: ROUTE_CUSTOMER_SERVICE })}
          buttonTitle={t('mijn_vnl.delete_account_overlay.title')}
        />
      </FormikWrapper>
    </div>
  );
};

export default OverviewAccountPage;
